import { sampleQueryKeys } from '@/config/queryKeys';
import { SAMPLES_ALLOWED_ROLES } from '@/config/samples';
import { getSelectedOrg } from '@/helpers';
import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { SampleWithNamesPublic } from '@/types';
import { hasOrgRole } from '@helpers/roles';
import { createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/samples/$sampleId')({
  beforeLoad: ({ context, location }) => {
    // PENDING must return before the org check: on a cold deep link /auth/me is still in flight
    // and no org has resolved yet, so checking it first sends an authorized user to /unauthorized.
    // See docs/ROUTE_PROTECTION.md.
    if (context.authentication.status === 'PENDING') return;

    // No user means no authorization decision is possible, so this belongs with /login rather
    // than /unauthorized — and it keeps the role gate below from being skipped silently.
    if (context.authentication.status === 'UNAUTHENTICATED' || !context.authentication.user) {
      throw redirect({ to: '/login' });
    }

    const selectedOrganization = getSelectedOrg();

    if (!selectedOrganization) {
      // setReferrerPath (not a raw sessionStorage write) so the store's own subscriber — the
      // /unauthorized page — actually picks up the value rather than reading a stale cache.
      useUnauthorizedReferrerStore.getState().setReferrerPath(location.pathname);
      throw redirect({ to: '/unauthorized' });
    }

    // Mirrors /samples' own gate: without it a user barred from the list could still reach a
    // sample by id, and this change is what puts that link in front of them.
    const rolesRaw = context.authentication.user.assumed_roles;
    if (!SAMPLES_ALLOWED_ROLES.some(role => hasOrgRole(rolesRaw, selectedOrganization.id, role))) {
      useUnauthorizedReferrerStore.getState().setReferrerPath(location.pathname);
      throw redirect({ to: '/unauthorized' });
    }
  },
  loader: ({ context: { queryClient }, params: { sampleId } }) => {
    const orgId = getSelectedOrg()?.id;
    if (!orgId) return;

    const detailKey = sampleQueryKeys.detail(orgId, sampleId);
    if (queryClient.getQueryData(detailKey)) return;

    // Seed the detail cache from the AG Grid list cache so arriving from the samples list
    // renders instantly instead of waiting on the single-sample GET the query falls back to on a
    // deep link or refresh. That GET's shape is asserted, not type-checked, until #96 deploys.
    const agGridPrefix = agGridDatasourceQueryKey(sampleQueryKeys.endpoints.listSearch(orgId));
    const cachedPageEntries = queryClient.getQueryCache().findAll({ queryKey: agGridPrefix });

    for (const entry of cachedPageEntries) {
      const page = entry.state.data as { total: number; records: SampleWithNamesPublic[] } | undefined;
      const match = page?.records?.find(s => s.id === sampleId);
      if (match) {
        // Inherit the source entry's own age instead of stamping "fresh now" — the grid cache's
        // staleTime is a day (createAgGridDatasource), so claiming freshness here would silently
        // suppress the detail query's 5s-staleTime refetch and could pin a day-old QC outcome.
        queryClient.setQueryData(detailKey, match, { updatedAt: entry.state.dataUpdatedAt });
        return;
      }
    }
  },
});
